import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Mic, Star, Code, Share2, Copy, Check, X, Heart, Sparkles } from "lucide-react";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { Seo } from "../components/Seo";

const API = "/api";
const SITE_URL = window.location.origin;

function faceAwareImage(url, w = 800, h = 600) {
  if (!url || !url.includes("res.cloudinary.com") || !url.includes("/upload/")) return url;
  return url.replace("/upload/", `/upload/c_fill,g_face,w_${w},h_${h},q_auto/`);
}

/* ---- Share & Embed Panel ---- */
function ShareEmbedPanel({ story, onClose }) {
  const [tab, setTab] = useState("share");
  const [copied, setCopied] = useState(null);

  const shareUrl = `${SITE_URL}/api/stories/${story.id}/share`;
  const storyUrl = `${SITE_URL}/story/${story.id}`;
  const whySnippet = story.why.length > 120 ? story.why.slice(0, 120) + "..." : story.why;

  const embedCode = `<div style="max-width:420px;font-family:system-ui,sans-serif;border:1px solid #e5e7eb;border-radius:16px;overflow:hidden;background:#fff">
${story.media_url && story.media_type === "image" ? `  <img src="${story.media_url}" alt="${story.name}" style="width:100%;height:220px;object-fit:cover"/>` : ""}
  <div style="padding:24px">
    <p style="margin:0;color:#E91E8C;font-size:11px;text-transform:uppercase;letter-spacing:0.18em;font-weight:700">${story.name}</p>
    <p style="margin:4px 0 14px;color:#4b5563;font-size:13px">${story.role}${story.city && story.country ? ` — ${story.city}, ${story.country}` : ""}</p>
    <p style="margin:0 0 16px;font-family:Georgia,serif;font-size:17px;line-height:1.6;color:#1f2937">&ldquo;${whySnippet}&rdquo;</p>
    <a href="${storyUrl}" target="_blank" rel="noopener" style="color:#E91E8C;font-size:12px;text-decoration:none;font-weight:700;text-transform:uppercase;letter-spacing:0.1em">Read full story on iambecause &rarr;</a>
  </div>
</div>`;

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label} copied!`);
      setTimeout(() => setCopied(null), 2000);
    } catch { toast.error("Failed to copy"); }
  };

  const socialLinks = [
    { name: "Twitter / X", icon: <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>, url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`"${whySnippet}" — ${story.name}`)}&url=${encodeURIComponent(shareUrl)}` },
    { name: "LinkedIn", icon: <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>, url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { name: "WhatsApp", icon: <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>, url: `https://wa.me/?text=${encodeURIComponent(`"${whySnippet}" — ${story.name}\n\nRead their story: ${shareUrl}`)}` },
    { name: "Facebook", icon: <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
  ];

  return (
    <div data-testid="share-embed-panel" className="mt-10 rounded-2xl border border-[#E5E7EB] bg-[#FBF7F0] overflow-hidden">
      <div className="flex border-b border-[#E5E7EB]">
        <button data-testid="tab-share" onClick={() => setTab("share")} className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-sans font-semibold transition-colors ${tab === "share" ? "text-[#E91E8C] bg-white border-b-2 border-[#E91E8C]" : "text-[#4B5563] hover:text-[#16130F]"}`}><Share2 size={16} />Share</button>
        <button data-testid="tab-embed" onClick={() => setTab("embed")} className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-sans font-semibold transition-colors ${tab === "embed" ? "text-[#E91E8C] bg-white border-b-2 border-[#E91E8C]" : "text-[#4B5563] hover:text-[#16130F]"}`}><Code size={16} />Embed</button>
      </div>
      <div className="p-6">
        {tab === "share" && (
          <div className="space-y-5">
            <p className="text-sm text-[#4B5563]">Share this story with a rich preview on any platform.</p>
            <div className="grid grid-cols-2 gap-3">
              {socialLinks.map((l) => (
                <a key={l.name} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`share-${l.name.toLowerCase().replace(/[\s\/]/g, "-")}`} className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-white text-sm font-sans font-medium text-[#16130F] hover:border-[#E91E8C]/30 hover:bg-[#E91E8C]/5 transition-all">{l.icon}{l.name}</a>
              ))}
            </div>
            <a
              href={`/api/stories/${story.id}/igcard`}
              download={`iambecause-${story.id}.png`}
              data-testid="download-ig-card"
              className="flex items-center justify-center gap-2.5 px-4 py-3.5 rounded-xl text-sm font-sans font-bold text-white transition-all hover:opacity-90"
              style={{ background: '#E91E8C' }}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0 5.838a6 6 0 100 12 6 6 0 000-12zm0 9.9a3.9 3.9 0 110-7.8 3.9 3.9 0 010 7.8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              download insta story card
            </a>
            <div className="flex gap-2">
              <div className="flex-1 rounded-xl border border-[#E5E7EB] bg-white px-3 py-2.5 text-sm text-[#4B5563] truncate font-mono">{shareUrl}</div>
              <button data-testid="copy-share-link" onClick={() => copyToClipboard(shareUrl, "Link")} className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#E91E8C] text-white text-sm font-semibold hover:bg-[#c2185b] transition-colors">{copied === "Link" ? <Check size={16} /> : <Copy size={16} />}{copied === "Link" ? "Copied" : "Copy"}</button>
            </div>
          </div>
        )}
        {tab === "embed" && (
          <div className="space-y-5">
            <p className="text-sm text-[#4B5563]">Copy this HTML and paste it into your blog, website, or newsletter.</p>
            <div className="rounded-xl border border-[#E5E7EB] bg-white overflow-hidden" style={{ maxWidth: 420 }}>
              {story.media_url && story.media_type === "image" && <img src={story.media_url} alt={story.name} className="w-full h-[180px] object-cover" />}
              <div className="p-5">
                <p className="text-[11px] uppercase tracking-[0.18em] font-sans font-bold text-[#E91E8C] mb-0.5">{story.name}</p>
                <p className="text-[13px] text-[#4B5563] mb-3">{story.role}{story.city && story.country ? ` — ${story.city}, ${story.country}` : ""}</p>
                <p className="font-sans text-[17px] leading-relaxed text-[#16130F] mb-3">&ldquo;{whySnippet}&rdquo;</p>
                <span className="text-[12px] uppercase tracking-[0.1em] font-sans font-bold text-[#E91E8C]">Read full story on iambecause &rarr;</span>
              </div>
            </div>
            <div className="relative">
              <pre className="rounded-xl border border-[#E5E7EB] bg-[#16130F] text-[#FBF7F0] p-4 text-xs overflow-x-auto max-h-48 font-mono leading-relaxed">{embedCode}</pre>
              <button data-testid="copy-embed-code" onClick={() => copyToClipboard(embedCode, "Embed code")} className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E91E8C] text-white text-xs font-semibold hover:bg-[#c2185b] transition-colors">{copied === "Embed code" ? <Check size={14} /> : <Copy size={14} />}{copied === "Embed code" ? "Copied" : "Copy"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- "This moved me" Reaction ---- */
function ReactionButton({ storyId, initialCount = 0 }) {
  const [reacted, setReacted] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [count, setCount] = useState(initialCount);

  // Reset state when storyId changes (navigating between stories)
  useEffect(() => {
    const storageKey = `reacted-${storyId}`;
    setReacted(localStorage.getItem(storageKey) === "true");
    setCount(initialCount);
    setAnimating(false);
  }, [storyId, initialCount]);

  const handleReact = async () => {
    if (reacted) return;
    setAnimating(true);
    try {
      await axios.post(`${API}/stories/${storyId}/react`);
      localStorage.setItem(`reacted-${storyId}`, "true");
      setReacted(true);
      setCount(c => c + 1);
    } catch { /* silent */ }
    setTimeout(() => setAnimating(false), 600);
  };

  const alreadyReacted = reacted && count > 0;

  return (
    <div className="space-y-2">
      <button
        data-testid="reaction-button"
        onClick={handleReact}
        disabled={reacted}
        className={`group inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-sans font-semibold transition-all duration-300 ${
          reacted
            ? "bg-[#E91E8C]/10 text-[#E91E8C] border border-[#E91E8C]/20 cursor-default"
            : "bg-white text-[#4B5563] border border-[#E5E7EB] hover:border-[#E91E8C]/40 hover:text-[#E91E8C] hover:bg-[#E91E8C]/5"
        }`}
      >
        <Heart
          size={18}
          className={`transition-all duration-300 ${
            reacted ? "fill-[#E91E8C] text-[#E91E8C]" : "group-hover:text-[#E91E8C]"
          } ${animating ? "scale-125" : ""}`}
        />
        felt this
      </button>
      {alreadyReacted && count > 1 && (
        <p className="text-xs font-sans text-[#4B5563]/70 pl-1">
          you and {count - 1} {count - 1 === 1 ? "other" : "others"} felt this
        </p>
      )}
      {alreadyReacted && count === 1 && (
        <p className="text-xs font-sans text-[#4B5563]/70 pl-1">
          you felt this
        </p>
      )}
      {!reacted && count > 0 && (
        <p className="text-xs font-sans text-[#4B5563]/70 pl-1">
          {count} {count === 1 ? "person" : "people"} felt this
        </p>
      )}
    </div>
  );
}

/* ---- Read Next Card ---- */
function ReadNextCard({ story }) {
  if (!story) return null;
  return (
    <div data-testid="read-next" className="mt-16 pt-10 border-t border-[#E5E7EB]">
      <p className="text-xs uppercase tracking-[0.2em] font-sans font-semibold text-[#4B5563] mb-6">
        Read next
      </p>
      <Link
        to={`/story/${story.id}`}
        data-testid="read-next-link"
        className="group flex flex-col md:flex-row gap-6 rounded-2xl border border-[#E5E7EB] overflow-hidden hover:shadow-lg transition-shadow duration-300"
      >
        {story.media_url && story.media_type === "image" && (
          <div className="md:w-48 h-40 md:h-auto flex-shrink-0 overflow-hidden amber-overlay">
            <img
              src={faceAwareImage(story.media_url, 400, 300)}
              alt={story.name}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        )}
        <div className="p-5 md:py-6 flex-1">
          <p className="text-xs uppercase tracking-[0.2em] font-sans font-semibold text-[#E91E8C] mb-1">
            {story.name}
          </p>
          <p className="text-xs text-[#4B5563] font-sans mb-3">{story.role}</p>
          <p className="font-sans text-base leading-relaxed text-[#16130F] line-clamp-2">
            &ldquo;{story.why}&rdquo;
          </p>
        </div>
      </Link>
    </div>
  );
}

/* ---- Main Story Page ---- */
export default function StoryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [nextStory, setNextStory] = useState(null);
  const [similarStories, setSimilarStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [storyRes, allRes] = await Promise.all([
          axios.get(`${API}/stories/${id}`),
          axios.get(`${API}/stories?limit=50`),
        ]);
        setStory(storyRes.data);
        // Find next story
        const all = allRes.data.stories || allRes.data;
        const currentIdx = all.findIndex((s) => s.id === id);
        if (currentIdx >= 0 && currentIdx < all.length - 1) {
          setNextStory(all[currentIdx + 1]);
        } else if (all.length > 1) {
          const other = all.find((s) => s.id !== id);
          setNextStory(other || null);
        }
        // Fetch similar stories
        try {
          const simRes = await axios.get(`${API}/stories/${id}/similar`);
          setSimilarStories(simRes.data || []);
        } catch {}
      } catch (e) {
        setError("Story not found.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div data-testid="story-loading" className="min-h-[80vh] flex items-center justify-center">
        <div className="animate-pulse space-y-4 max-w-2xl w-full px-6">
          <div className="h-8 w-48 bg-[#E5E7EB] rounded" />
          <div className="h-64 bg-[#FBF7F0] rounded-3xl" />
          <div className="h-6 w-full bg-[#E5E7EB] rounded" />
          <div className="h-6 w-3/4 bg-[#E5E7EB] rounded" />
        </div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div data-testid="story-error" className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="font-sans text-xl text-[#4B5563] mb-6">{error || "Story not found."}</p>
          <button onClick={() => navigate("/")} className="btn-amber">Back to Stories</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="story-page" className="min-h-screen bg-white">
      <Seo
        title={`${story.name} — ${story.role}`}
        description={story.why ? (story.why.length > 155 ? story.why.slice(0, 155) + "…" : story.why) : undefined}
        path={`/story/${story.id}`}
        image={story.media_url && story.media_type === "image" ? story.media_url : undefined}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${story.name} — why I do what I do`,
          description: story.why ? story.why.slice(0, 200) : undefined,
          image: story.media_url && story.media_type === "image" ? story.media_url : undefined,
          author: { "@type": "Person", name: story.name },
          publisher: { "@type": "Organization", name: "iambecause", url: "https://iambecause.com" },
          datePublished: story.approved_at || story.created_at,
          mainEntityOfPage: `https://iambecause.com/story/${story.id}`,
        }}
      />
      <Toaster position="top-center" richColors />

      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-8">
        <Link to="/" data-testid="back-to-stories" className="inline-flex items-center gap-2 text-sm font-sans text-[#4B5563] hover:text-[#16130F] transition-colors">
          <ArrowLeft size={16} /> All stories
        </Link>
      </div>

      <article className="max-w-4xl mx-auto px-6 md:px-12 py-8 md:py-12">
        {/* Media */}
        {story.media_url && (
          <div className="mb-10 rounded-3xl overflow-hidden amber-overlay">
            {story.media_type === "image" && <img src={faceAwareImage(story.media_url, 1200, 900)} alt={story.name} data-testid="story-image" className="w-full" />}
            {story.media_type === "video" && <video src={story.media_url} data-testid="story-video" controls className="w-full max-h-[70vh]" />}
            {story.media_type === "audio" && (
              <div data-testid="story-audio" className="audio-placeholder rounded-3xl p-8">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-[#E91E8C]/10 flex items-center justify-center"><Mic size={32} className="text-[#E91E8C]" /></div>
                  <audio src={story.media_url} controls className="w-full max-w-md" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Header */}
        <header className="mb-10">
          <div className="flex items-start justify-between gap-4">
            <div>
              {story.featured && (
                <div className="flex items-center gap-2 mb-4">
                  <Star size={14} className="text-[#E91E8C] fill-[#E91E8C]" />
                  <span className="text-xs uppercase tracking-[0.2em] font-sans font-semibold text-[#E91E8C]">Featured Story</span>
                </div>
              )}
              <h1 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#E91E8C] mb-1">{story.name}</h1>
              {!story.anonymous && <p className="text-sm font-sans text-[#4B5563]">{story.role}</p>}
              {story.stage === "seeking" && (
                <p data-testid="seeking-label" className="text-sm mt-1" style={{ fontFamily: "var(--genz-font-display)", fontStyle: 'italic', color: '#E91E8C' }}>
                  Still finding my why
                </p>
              )}
              {(story.city || story.country) && (
                <div className="flex items-center gap-1.5 mt-2 text-sm text-[#4B5563]/70">
                  <MapPin size={14} />
                  <span>{[story.city, story.country].filter(Boolean).join(", ")}</span>
                </div>
              )}
            </div>

            {/* Share dropdown */}
            <div className="relative">
              <button data-testid="toggle-share-embed" onClick={() => setShowShare(!showShare)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-sans font-semibold transition-all ${showShare ? "bg-[#E91E8C] text-white" : "bg-[#E91E8C]/10 text-[#E91E8C] hover:bg-[#E91E8C]/20"}`}>
                {showShare ? <X size={16} /> : <Share2 size={16} />}
                {showShare ? "Close" : "Share"}
              </button>

              {showShare && (() => {
                const shareUrl = `${SITE_URL}/api/stories/${story.id}/share`;
                const whySnippet = story.why.length > 100 ? story.why.slice(0, 100) + "..." : story.why;
                const shareText = `"${whySnippet}" — ${story.name}`;
                return (
                  <div data-testid="share-dropdown" className="absolute right-0 top-12 z-50 w-64 bg-white rounded-2xl border border-[#E5E7EB] shadow-xl p-4 space-y-2">
                    <a href={`https://wa.me/?text=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FBF7F0] transition-colors text-sm font-sans text-[#16130F]">
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#25D366]"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      WhatsApp
                    </a>
                    <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FBF7F0] transition-colors text-sm font-sans text-[#16130F]">
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#16130F]"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      Twitter / X
                    </a>
                    <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FBF7F0] transition-colors text-sm font-sans text-[#16130F]">
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#0A66C2]"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                      LinkedIn
                    </a>
                    <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FBF7F0] transition-colors text-sm font-sans text-[#16130F]">
                      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#1877F2]"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      Facebook
                    </a>
                    <div className="border-t border-[#E5E7EB] pt-2 mt-1">
                      <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FBF7F0] transition-colors text-sm font-sans text-[#16130F] w-full">
                        <Copy size={18} className="text-[#4B5563]" />
                        Copy link
                      </button>
                      <a
                        href={`/api/stories/${story.id}/igcard`}
                        download={`iambecause-${story.id}.png`}
                        data-testid="download-ig-card"
                        className="flex items-center justify-center gap-2.5 px-3 py-2.5 mt-1 rounded-xl text-sm font-sans font-bold text-white transition-all hover:opacity-90"
                        style={{ background: '#E91E8C' }}
                      >
                        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0 5.838a6 6 0 100 12 6 6 0 000-12zm0 9.9a3.9 3.9 0 110-7.8 3.9 3.9 0 010 7.8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                        download insta story card
                      </a>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </header>

        {/* Body */}
        <div data-testid="story-body" className="max-w-2xl">
          <blockquote dir="auto" className="font-sans text-lg md:text-xl text-[#16130F]" style={{ lineHeight: '1.9' }}>
            {story.why.split("\n").map((paragraph, i) => (
              <p key={i} className={i > 0 ? "mt-6" : ""}>
                {i === 0 && <span className="text-[#E91E8C]">&ldquo;</span>}
                {paragraph}
                {i === story.why.split("\n").length - 1 && <span className="text-[#E91E8C]">&rdquo;</span>}
              </p>
            ))}
          </blockquote>

          {/* Voice transcription label */}
          {story.media_type === "voice" && (
            <p className="mt-4 text-xs font-sans" style={{ color: 'var(--iab-charcoal-pale)' }}>
              <Mic size={12} className="inline mr-1" style={{ color: '#E91E8C' }} />
              Shared by voice · Transcribed by Mira
            </p>
          )}

          {/* Mira chat label */}
          {story.media_type === "mira_chat" && (
            <p className="mt-4 text-xs font-sans" style={{ color: 'var(--iab-charcoal-pale)' }}>
              <Star size={12} className="inline mr-1" style={{ color: '#E91E8C' }} />
              Shared through Mira
            </p>
          )}

          {/* Seeking story note */}
          {story.stage === "seeking" && (
            <div data-testid="seeking-note" className="mt-8">
              <p className="font-sans text-sm italic" style={{ color: '#E91E8C' }}>
                This story is still becoming. That is why it belongs here.
              </p>
              {story.mira_reflection && (
                <blockquote className="mt-4 border-l-2 pl-4" style={{ borderColor: '#E91E8C' }}>
                  <p className="font-sans text-sm italic" style={{ color: '#4B5563' }}>
                    &ldquo;{story.mira_reflection}&rdquo;
                  </p>
                  <p className="text-xs mt-1" style={{ color: '#E91E8C' }}>— Mira</p>
                </blockquote>
              )}
              <button
                data-testid="talk-to-mira-seeking"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('openMiraSeeking'));
                }}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-sans font-medium transition-all hover:scale-105"
                style={{ border: '1px solid rgba(233,30,140,0.3)', color: '#E91E8C', background: 'rgba(233,30,140,0.06)' }}
              >
                <Sparkles size={12} />
                I'm still searching too — talk to Mira
              </button>
            </div>
          )}
        </div>

        {/* Reaction */}
        <div className="mt-12">
          <ReactionButton storyId={story.id} initialCount={story.reactions || 0} />
        </div>

        {/* Read Next */}
        <ReadNextCard story={nextStory} />

        {/* Similar Whys */}
        {similarStories.length > 0 && (
          <div data-testid="similar-stories" className="mt-12 pt-10" style={{ borderTop: '0.5px solid var(--iab-border)' }}>
            <p className="text-xs uppercase tracking-[0.25em] font-sans font-semibold mb-6" style={{ color: 'var(--iab-amber)' }}>
              Others who share this why
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {similarStories.map((s) => (
                <Link
                  key={s.id}
                  to={`/story/${s.id}`}
                  data-testid={`similar-story-${s.id}`}
                  className="block group p-5 transition-all hover:shadow-md"
                  style={{ border: '0.5px solid var(--iab-border)', background: 'var(--iab-white)' }}
                >
                  <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold mb-2" style={{ color: 'var(--iab-amber)' }}>
                    {s.name}
                  </p>
                  <p className="text-sm font-sans mb-3" style={{ color: 'var(--iab-charcoal-soft)' }}>
                    {s.role}
                  </p>
                  <p dir="auto" className="font-sans text-sm line-clamp-3" style={{ color: 'var(--iab-charcoal)', lineHeight: '1.8' }}>
                    &ldquo;{s.pull_quote || s.why?.slice(0, 120)}&rdquo;
                  </p>
                  {s.themes && s.themes.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {s.themes.slice(0, 2).map((t) => (
                        <span key={t} className="text-xs font-sans px-2 py-0.5 rounded-full" style={{ background: 'rgba(233,30,140,0.08)', color: 'var(--iab-amber)' }}>
                          {t.replace(/-/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 pt-10 border-t border-[#E5E7EB]">
          <p className="font-sans text-lg text-[#4B5563] mb-4">Inspired? Share your own story.</p>
          <button
            data-testid="story-page-cta"
            onClick={() => {
              const orb = document.querySelector('[data-testid="mira-orb"]');
              if (orb) orb.click();
            }}
            className="btn-amber inline-flex"
          >
            Talk to Mira <ArrowRight size={16} />
          </button>
          <p className="mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '11px', color: '#888780', fontStyle: 'italic' }}>
            Mira is the one who knows — she listens, she asks, she remembers.
          </p>
        </div>

        {/* Invite someone */}
        <div className="mt-10 rounded-2xl bg-[#FBF7F0] border border-[#E5E7EB] p-6 md:p-8 text-center">
          <p className="font-sans text-lg md:text-xl text-[#16130F] mb-1">
            Know someone whose why deserves to be heard?
          </p>
          <p className="text-sm text-[#4B5563] mb-5">Send them this. It takes two minutes.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              data-testid="invite-whatsapp"
              href={`https://wa.me/?text=${encodeURIComponent("Someone thinks you have a story worth telling.\n\niambecause asks one question: Why do you do what you do?\n\nShare yours: " + SITE_URL + "/submit")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-semibold hover:bg-[#1da851] transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Send via WhatsApp
            </a>
            <button
              data-testid="invite-copy-link"
              onClick={() => {
                navigator.clipboard.writeText(SITE_URL + "/submit");
                toast.success("Link copied!");
              }}
              className="btn-ghost !py-2.5 !px-5 text-sm"
            >
              Copy invite link
            </button>
          </div>
        </div>
      </article>
    </div>
  );
}
